import {
  fmtMult,
  spotAffinityLabel,
  spotsIn,
  TRAINING_SPOT_KINDS,
  TRAINING_SPOTS,
  type TrainingSpotDef,
  type World,
  type WorldEvent,
} from '@nine/shared';
import { iconEl, spotIcon } from '../render/icons';
import { $, h, setText } from './dom';

/** Jade, the colour the training spots are marked in on the maps and the HUD. */
export const SPOT_COLOR = '#6fe0b4';

/** What the place gives and to whom, for tooltips: "ฝึกปรือ ×1.5 · กำลังภายในธาตุอ่อน ×2". */
export function spotBonusText(s: TrainingSpotDef): string {
  const who = spotAffinityLabel(s);
  return `ฝึกปรือ ${fmtMult(s.mult)}${who && s.affinity ? ` · ${who} ${fmtMult(s.affinity.mult)}` : ''}`;
}

/**
 * The small line in the player's frame while they stand in a training spot (data/trainingSpots.ts):
 * the place, the multiplier it gives them now, and whether it suits them or is held back.
 */
export class SpotBadge {
  private readonly root: HTMLElement;
  private readonly name = h('b', { class: 'sp-name' });
  private readonly mult = h('span', { class: 'sp-mult' });
  private readonly note = h('span', { class: 'sp-note' });
  /** The kind of place (waterfall, cave, peak…) as its 16 px icon (icons/spot). */
  private readonly kind = h('span', { class: 'sp-kind', ariaHidden: 'true' });
  private shown = '';

  constructor(private readonly world: World) {
    this.root = h(
      'div',
      { id: 'spotBadge', hidden: true },
      this.kind,
      h('span', { class: 'sp-tag' }, 'ที่ฝึก'),
      this.name,
      h('span', { class: 'sp-gain' }, this.mult, this.note),
    );
    $('#training').after(this.root);
  }

  sync(): void {
    const st = this.world.spotState();
    this.root.hidden = !st;
    if (!st) return;
    const s = st.spot;
    if (this.shown !== s.id) {
      this.shown = s.id;
      this.root.title = `${s.name} (${TRAINING_SPOT_KINDS[s.kind]}) · ${spotBonusText(s)}\n${s.desc}`;
      this.kind.replaceChildren(iconEl(spotIcon(s.kind, 16), 'ico16'));
    }
    setText(this.name, s.name);
    setText(this.mult, st.paused ? 'หยุดชั่วคราว' : `ฝึกปรือ ${fmtMult(st.mult)}`);
    const who = spotAffinityLabel(s);
    setText(
      this.note,
      st.paused ??
        (st.suits && who
          ? `เหมาะกับ${who}`
          : who && s.affinity
            ? `${who} ได้ ${fmtMult(s.affinity.mult)}`
            : ''),
    );
    this.root.classList.toggle('paused', !!st.paused);
    this.root.classList.toggle('suits', st.suits && !st.paused);
  }
}

/** The banner on stepping into a spot and the toast on stepping out, or null for other events. */
export function spotMessage(ev: WorldEvent, world: World): { banner?: string; toast?: string } | null {
  if (ev.type !== 'trainingSpot') return null;
  const st = world.spotState();
  if (ev.id && st) {
    const who = st.suits ? spotAffinityLabel(st.spot) : null;
    return {
      banner: `เข้าสู่ ${st.spot.name} · ฝึกปรือ ${fmtMult(st.paused ? st.spot.mult : st.mult)}${who ? ` (${who})` : ''}`,
    };
  }
  const from = TRAINING_SPOTS.find((s) => s.id === ev.from);
  return from ? { toast: `ออกจาก ${from.name}` } : null;
}

/** Marks this zone's training spots on a map drawn at `px` pixels a tile: a jade ring and diamond. */
export function drawSpotMarks(
  g: CanvasRenderingContext2D,
  world: World,
  at: (x: number, y: number) => readonly [number, number],
  px: number,
): void {
  if (world.interiors.inside) return;
  for (const s of spotsIn(world.map.zone)) {
    const [x, y] = at(s.area.x, s.area.y);
    const r = Math.max(2, Math.round(s.area.r * px));
    g.fillStyle = 'rgba(111,224,180,.18)';
    g.fillRect(x - r, y - r, r * 2, r * 2);
    g.fillStyle = SPOT_COLOR;
    for (let k = -2; k <= 2; k++) g.fillRect(x + k, y - (2 - Math.abs(k)), 1, (2 - Math.abs(k)) * 2 + 1);
  }
}
